import { ReactNode, useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { loadPortalConfig, PortalConfig } from "../api/portalConfig";
import { setOktaAuthFromConfig } from "../auth/oktaConfig";

// Loads /api/portal-config once at app bootstrap, wires the module-
// level OktaAuth holder (api/client.ts reads from it without going
// through React), and only then renders children. Without this,
// neither the <Security> wrapper nor fetchWithAuth would have
// anything to point at.
//
// Three render states:
//   loading → small centered spinner. Fast — single GET to the same
//             origin. If users see this for more than a beat the
//             portal backend is down anyway.
//   error   → hard-fail screen. Means the backend is unreachable or
//             returning garbage; the SPA can't do anything useful
//             without the bootstrap.
//   ready   → renders children.

interface Props {
  children: ReactNode;
}

export function PortalConfigProvider({ children }: Props) {
  const [config, setConfig] = useState<PortalConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadPortalConfig()
      .then((cfg) => {
        if (cancelled) return;
        setOktaAuthFromConfig(cfg.auth);
        setConfig(cfg);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
          p: 4,
        }}
      >
        <Typography variant="h5" color="error">
          Portal config unreachable
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          The backend may be down. Refresh once it's healthy.
        </Typography>
      </Box>
    );
  }

  if (!config) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return <>{children}</>;
}
